import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconCheck } from './icons';

type CopyState = 'idle' | 'copied' | 'failed';

export interface CopyLinkControlProps {
  getUrl: () => Promise<string> | string;
  children: ReactNode;
  className?: string;
  buttonClassName?: string;
  title?: string;
  ariaLabel?: string;
  copiedMessage?: string;
  errorMessage?: string;
  /** Called after a successful copy. */
  onCopied?: () => void;
}

export function CopyLinkControl({
  getUrl,
  children,
  className = 'story-permalink-control',
  buttonClassName = 'feed-item-action-btn',
  title,
  ariaLabel,
  copiedMessage = 'Link copied',
  errorMessage = 'Could not copy link',
  onCopied,
}: CopyLinkControlProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const resetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
  }, []);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const url = await getUrl();
      // Chrome leaves writeText pending indefinitely when the document is not
      // focused, so a bare await would leave the button with no feedback at
      // all. Time out instead and report the failure.
      await Promise.race([
        navigator.clipboard.writeText(url),
        new Promise((_, reject) => window.setTimeout(() => reject(new Error('timeout')), 1500)),
      ]);
      setCopyState('copied');
      onCopied?.();
    } catch {
      setCopyState('failed');
    }
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setCopyState('idle'), 2500);
  };

  const message = copyState === 'copied'
    ? copiedMessage
    : copyState === 'failed'
      ? errorMessage
      : '';

  const buttonClass = [buttonClassName, copyState === 'copied' ? 'active' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <span className={className}>
      <button
        type="button"
        className={buttonClass}
        onClick={handleCopy}
        title={copyState === 'copied' ? copiedMessage : title}
        aria-label={copyState === 'copied' ? copiedMessage : ariaLabel || title}
      >
        {children}
        {copyState === 'copied' && <span className="copy-link-check"><IconCheck size={14} /></span>}
      </button>
      {copyState === 'copied' && <span className="copy-link-sr-status" role="status">{message}</span>}
      {copyState === 'failed' && (
        <span className="story-permalink-message error" role="status">
          {message}
        </span>
      )}
    </span>
  );
}
